import { useMemo, useState, type ComponentType } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CalendarRange, CircleDollarSign, Clock, FileText, Plus, Wallet } from 'lucide-react';
import { financeV2Service } from '../services/financeV2Service';
import CreateAnnualFeeModal from '../components/CreateAnnualFeeModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/data-table';
import { AmountText, FinanceStatusBadge, type AmountTone } from '@/components/finance-v2/FinanceBadges';

import { getErrorMessage } from '../utils/error.utils'
import type { AnnualFee } from '../types/financeV2.types';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  ACTIVE: 'Ativa',
  PAID: 'Paga',
  CANCELLED: 'Cancelada',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

function StatCard({ label, value, icon: Icon, tone = 'neutral' }: {
  label: string;
  value: string | number;
  icon: ComponentType<{ className?: string }>;
  tone?: AmountTone;
}) {
  return (
    <Card size="sm">
      <CardContent className="flex items-center gap-3">
        <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0">
          <p className="truncate text-xs text-muted-foreground">{label}</p>
          <AmountText tone={tone} className="block truncate text-lg font-semibold">{value}</AmountText>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FinanceV2AnnualFeesPage() {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Buscar anuidades
  const { data: annualFeesData, isLoading } = useQuery({
    queryKey: ['annual-fees', selectedYear],
    queryFn: () => financeV2Service.annualFees.list({ year: selectedYear }),
  });

  // Buscar estatísticas
  const { data: statsData } = useQuery({
    queryKey: ['annual-fees-stats', selectedYear],
    queryFn: () => financeV2Service.annualFees.getStatistics(selectedYear),
  });

  // Mutation para cancelar
  const cancelMutation = useMutation({
    mutationFn: (id: string) => financeV2Service.annualFees.cancel(id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annual-fees'] });
      alert('Anuidade cancelada com sucesso!');
    },
    onError: (err: unknown) => {
      alert(getErrorMessage(err, 'Erro ao cancelar anuidade'));
    },
  });

  const annualFees = annualFeesData?.data?.annual_fees || [];
  const stats = statsData?.data?.statistics;

  const columns = useMemo<ColumnDef<AnnualFee>[]>(() => [
    {
      accessorKey: 'client_name',
      header: 'Cliente',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{row.original.client_name}</div>
          <div className="text-xs text-muted-foreground">{row.original.client_document}</div>
        </div>
      ),
    },
    {
      accessorKey: 'year',
      header: 'Ano',
      cell: ({ row }) => <span className="text-sm tabular-nums">{row.original.year}</span>,
    },
    {
      accessorKey: 'total_amount',
      header: 'Valor Total',
      cell: ({ row }) => (
        <AmountText tone="neutral" className="text-sm">{formatCurrency(row.original.total_amount)}</AmountText>
      ),
    },
    {
      id: 'installments_summary',
      header: 'Parcelas',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {row.original.paid_installments || 0}/{row.original.installments}
        </span>
      ),
    },
    {
      accessorKey: 'completion_percentage',
      header: 'Progresso',
      cell: ({ row }) => {
        const pct = row.original.completion_percentage || 0;
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 rounded-full bg-muted">
              <div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm tabular-nums text-muted-foreground">{pct.toFixed(0)}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <FinanceStatusBadge status={row.original.status}>{STATUS_LABELS[row.original.status]}</FinanceStatusBadge>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.status !== 'CANCELLED' && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (confirm('Deseja cancelar esta anuidade?')) {
                cancelMutation.mutate(row.original.id);
              }
            }}
          >
            Cancelar
          </Button>
        ),
    },
  ], [cancelMutation]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <CalendarRange className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Anuidades</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gestão de anuidades de clientes</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Nova Anuidade
        </Button>
      </div>

      {stats && (
        <KpiGrid cols={4} gap={3}>
          <StatCard label="Total Contratado" value={formatCurrency(stats.total_amount_contracted)} icon={FileText} />
          <StatCard label="Total Recebido" value={formatCurrency(stats.total_amount_received)} icon={Wallet} tone="income" />
          <StatCard label="Pendente" value={formatCurrency(stats.total_amount_pending)} icon={Clock} />
          <StatCard label="Total de Anuidades" value={stats.total_annual_fees} icon={CircleDollarSign} />
        </KpiGrid>
      )}

      <div className="flex flex-col gap-1.5 sm:w-48">
        <Label htmlFor="annual-fees-year">Ano</Label>
        <NativeSelect
          id="annual-fees-year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
        </NativeSelect>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 p-2">
            <DataTable
              columns={columns}
              data={annualFees}
              minWidth="min-w-[720px]"
              emptyMessage="Nenhuma anuidade encontrada"
            />
          </div>
        </Card>
      )}

      <CreateAnnualFeeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
}
